import React, { useMemo, useEffect } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { canAccessTab } from "@/lib/permissions";

const BASE = "/race-tracker";

const TOGGLE_ITEMS = [
  { path: BASE, label: "Client Dashboard", end: true, tabId: "dashboard" },
  { path: `${BASE}/list`, label: "List View", tabId: "list" },
  { path: `${BASE}/analytics`, label: "Analytics", tabId: "analytics" },
  { path: `${BASE}/intel-sweep`, label: "Intel Sweep", tabId: "intel-sweep" },
  { path: `${BASE}/import`, label: "Import", tabId: "import" },
  { path: `${BASE}/settings`, label: "Settings", tabId: "settings" },
];

function pathMatches(pathname, target, end) {
  if (end) return pathname === target || pathname === `${target}/`;
  return pathname === target || pathname.startsWith(`${target}/`);
}

export default function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { rolePagePermissions, isSuperAdmin } = useAuth();

  const visibleItems = useMemo(
    () => TOGGLE_ITEMS.filter((item) => canAccessTab("race-tracker", item.tabId, rolePagePermissions, isSuperAdmin)),
    [rolePagePermissions, isSuperAdmin],
  );

  // Redirect to first accessible tab if the current path is not permitted
  useEffect(() => {
    if (visibleItems.length === 0) return;
    const currentItemAllowed = visibleItems.some((item) =>
      pathMatches(location.pathname, item.path, !!item.end)
    );
    if (!currentItemAllowed) {
      navigate(visibleItems[0].path, { replace: true });
    }
  }, [visibleItems, location.pathname, navigate]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="sticky top-0 z-50 border-b border-border bg-card shadow-sm">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center h-14 gap-4 flex-wrap">
          <Link to={BASE} className="flex items-center gap-2 mr-4 shrink-0">
            <div
              className="w-8 h-8 rounded-md flex items-center justify-center"
              style={{ backgroundColor: "hsl(var(--smg-accent-yellow))" }}
            >
              <span className="text-[10px] font-black text-gray-900 tracking-tight">SMG</span>
            </div>
            <span className="text-sm font-semibold tracking-tight text-foreground hidden sm:block">
              Competitive Intelligence
            </span>
          </Link>

          <div className="flex items-center flex-1 min-w-0 justify-center sm:justify-start overflow-x-auto rounded-md border border-border bg-muted p-0.5 gap-0.5">
            {visibleItems.map(({ path, label, end }) => {
              const isActive = pathMatches(location.pathname, path, !!end);
              return (
                <Link
                  key={path}
                  to={path}
                  className={`px-2.5 sm:px-3 py-1.5 rounded text-xs font-medium transition-all whitespace-nowrap shrink-0 ${
                    isActive
                      ? "bg-white text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {label}
                </Link>
              );
            })}
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-[1440px] w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Outlet />
      </main>
    </div>
  );
}
